import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Dialog, Picker } from 'antd-mobile';
import type { PickerValue } from 'antd-mobile/es/components/picker';
import { UpOutline, DownOutline } from 'antd-mobile-icons';
import { ScanInput } from '../../pda/components/ScanInput';
import { Pda, PdaScanEvent } from '../../pda/components/Pda';
import { useScanQueueConsumer } from '../../pda/components/useScanQueueConsumer';
import { Header } from '../../pda/components/Header';
import { ResultTable, Column, createRecord } from '../../pda/components/ResultTable';
import { DestinationSelector } from './components/DestinationSelector';
import { CarrierSelector } from './components/CarrierSelector';
import { isBagNumber, isTtBagNumber, isTrackingNumber, showBarCodeError } from '../../../utils/BarCodeUtil';
import { showError, clearCurrentError } from '../../../utils/newExceptionUtils';
import { parseException } from '../../../utils/exceptionUtils';
import { playScanFailedAoSound } from '../../../utils/scanUtils';
import { formatLocalizedTime } from '../../../utils/dateFormatUtils';
import { GET_BAG_BASIC_INFO_BY_NUMBER, GetBagBasicInfoByNumberResponse, GetBagBasicInfoByNumberVariables, BagBasicInfoType, GET_ALL_CARRIER_OPTIONS, GetAllCarrierOptionsResponse, HANDLE_WHS_BAGGING_SCAN_EVENT, HandleWhsBaggingScanEventVariables, HandleWhsBaggingScanEventResponse, BaggingScanCandidateBagInput, BagContainerCandidateType, BAG_STATUS_DISCARDED, BAG_STATUS_HANDOVER, UPDATE_BAG_CONTAINER_PDA, UpdateBagContainerVariables, UpdateBagContainerPdaResponse } from '../../../graphql/queries/bagQueries';
import { WarehouseDetailType, GET_WAREHOUSE_DETAILS_BY_DESTINATION, GetWarehouseDetailsByDestinationResponse, GetWarehouseDetailsByDestinationVariables, GET_ACCOUNT_BIND_WAREHOUSE, GetAccountBindWarehouseResponse } from '../../../graphql/queries/warehouseQueries';
import { getClientReporter } from '../../../utils/clientReport/clientReporterRegistry';
import { ClientReportOpType } from '../../../utils/clientReport/clientReportTypes';
import { safeJsonStringify, truncateToLength } from '../../../utils/clientReport/clientReportUtils';
import { ScanResultEnvelope, ScanResultPrompt, useScanResultPrompt, ClientError, buildClientError } from '../../pda/scanResult';
import type { BaggingScanResponse } from '../../../graphql/queries/bagQueries';

// 存储目的地详细信息，key 是 sortingCode，value 是 WarehouseDetailType
interface DestinationInfo {
    [sortingCode: string]: WarehouseDetailType;
}

const STORAGE_KEY = 'bagging_scan_destinations';
const STORAGE_LOCK_KEY = 'bagging_scan_destinations_locked';
const STORAGE_CARRIER_KEY = 'bagging_scan_carriers';
const STORAGE_CARRIER_LOCK_KEY = 'bagging_scan_carriers_locked';

const DEFAULT_BORDER_COLOR = 'transparent'; // 默认透明
const SUCCESS_BORDER_COLOR = 'rgb(34, 197, 94)'; // green-500

/** 打印模板类型：1-SwiftX 2-TT */
const PRINT_TYPE_SWIFTX = 1;

interface ScannedBag {
    id: string;
    // 后端包牌主键（bag.id）；用于 UPDATE_BAG_CONTAINER 等按 id 定位的 mutation。
    // 通过 GET_BAG_BASIC_INFO_BY_NUMBER 返回并在扫码入库时填入；老数据/异常路径可能为 null，调用前需判空。
    backendBagId?: number | null;
    destination: string;
    bagCode: string; // 显示用：BAG 前缀用内部包牌号，其余（TT/外部）用原始扫描值
    internalBagNumber: string; // 传后端用：始终为内部包牌号（validBagInfo.bagNumber）
    time: string;
    sortingCode?: string; // 存储包牌的sortingCode，用于判断是否需要替换
    carrierCode?: number | null; // 包牌尾程商，用于尾程商筛选
    fromLocation?: string | null; // TT 包牌：始发，用于列表展示
    nextStop?: string | null; // TT 包牌：下一站，用于列表展示
    printType?: number | null; // 打印模板类型，TT+SWX 时按分拣码参与替换
    ttBizType?: string | null; // TT分拣业务类型：FBT/CBT/UNLIMITED，用于列表展示
    promptSortingCode?: string | null; // TT 包牌：SortingRule 与预分拣码拼接串，用于列表展示
    // PPMOD 装包：本地展示 + 装包 mutation 透传，非 PP 场景 candidates 为空 → 不渲染下拉
    isPPMOD?: boolean;
    bagContainerId?: number | null; // 前端选择/后端已绑的容器 id
    containerCandidates?: BagContainerCandidateType[]; // 后端 getBagBasicInfoByNumber 返回；空 → 不展示下拉
}


interface PageBodyProps {
    handleScanStart: () => void;
    handleScanSuccess: () => void;
    scanPrompt: ReturnType<typeof useScanResultPrompt>;
}

const baggingScanClientReporter = getClientReporter(ClientReportOpType.BAGGING_SCAN);

const PageBody: React.FC<PageBodyProps> = ({ handleScanStart, handleScanSuccess, scanPrompt }) => {
    const { t } = useTranslation();
    const [carrierSelectedValues, setCarrierSelectedValues] = useState<number[]>([]);
    const [isCarrierLocked, setIsCarrierLocked] = useState(false);
    const [destinations, setDestinations] = useState<string[]>([]);
    const [isLocked, setIsLocked] = useState(false);
    const [scanValue, setScanValue] = useState('');
    const [, setBagNumber] = useState(''); // 当前绑定的包牌（仅 setter 使用）
    const [scannedBags, setScannedBags] = useState<ScannedBag[]>([]); // 已扫描的包牌列表
    const [isScannedBagsExpanded, setIsScannedBagsExpanded] = useState(true); // 已扫描包牌列表是否展开
    // 当前打开容器 Picker 的 bag id；同一时刻仅一个 bag 的 Picker 可见
    const [containerPickerBagId, setContainerPickerBagId] = useState<string | null>(null);
    const [successRecords, setSuccessRecords] = useState<any[]>([]);
    const [failedRecords, setFailedRecords] = useState<any[]>([]);
    const [destinationInfoMap, setDestinationInfoMap] = useState<DestinationInfo>({}); // 目的地信息映射
    const scanInputRef = useRef<HTMLInputElement>(null);

    const { data: carrierOptionsData } = useQuery<GetAllCarrierOptionsResponse>(GET_ALL_CARRIER_OPTIONS);
    const carrierOptions = carrierOptionsData?.getAllCarrierOptions ?? [];
    const { data: warehouseData } = useQuery<GetAccountBindWarehouseResponse>(GET_ACCOUNT_BIND_WAREHOUSE);
    const currentWarehouseId = warehouseData?.warehouse?.id ?? null;
    const [getBagBasicInfo] = useLazyQuery<GetBagBasicInfoByNumberResponse, GetBagBasicInfoByNumberVariables>(GET_BAG_BASIC_INFO_BY_NUMBER);
    const [getWarehouseDetailsByDestination] = useLazyQuery<GetWarehouseDetailsByDestinationResponse, GetWarehouseDetailsByDestinationVariables>(GET_WAREHOUSE_DETAILS_BY_DESTINATION);
    const [handleWhsBaggingScanEvent] = useMutation<HandleWhsBaggingScanEventResponse, HandleWhsBaggingScanEventVariables>(HANDLE_WHS_BAGGING_SCAN_EVENT);
    // 与 PC 包牌列表同一入口：换容器立即落库；失败时回滚本地 state
    const [updateBagContainerPda] = useMutation<UpdateBagContainerPdaResponse, UpdateBagContainerVariables>(UPDATE_BAG_CONTAINER_PDA);

    // onProcess 内读最新 state 的 ref 镜像（render body 同步赋值）
    const scannedBagsRef = useRef(scannedBags);
    scannedBagsRef.current = scannedBags;
    const carrierSelectedValuesRef = useRef(carrierSelectedValues);
    carrierSelectedValuesRef.current = carrierSelectedValues;
    const destinationsRef = useRef(destinations);
    destinationsRef.current = destinations;
    const destinationInfoMapRef = useRef(destinationInfoMap);
    destinationInfoMapRef.current = destinationInfoMap;
    const currentWarehouseIdRef = useRef(currentWarehouseId);
    currentWarehouseIdRef.current = currentWarehouseId;
    const handleScanStartRef = useRef(handleScanStart);
    handleScanStartRef.current = handleScanStart;
    const handleScanSuccessRef = useRef(handleScanSuccess);
    handleScanSuccessRef.current = handleScanSuccess;
    // 当前打开的冲突 Dialog handle，弹新冲突 Dialog 前用于关闭旧的
    const dialogHandleRef = useRef<ReturnType<typeof Dialog.show> | null>(null);

    // 卸载时关闭可能悬挂的 Dialog（Dialog 是 portal 到 body 的，不会随组件卸载消失）
    useEffect(() => {
        return () => {
            dialogHandleRef.current?.close();
            dialogHandleRef.current = null;
        };
    }, []);

    // 从localStorage恢复状态
    useEffect(() => {
        const savedDestinations = localStorage.getItem(STORAGE_KEY);
        const savedLocked = localStorage.getItem(STORAGE_LOCK_KEY);
        
        if (savedDestinations) {
            try {
                const parsed = JSON.parse(savedDestinations);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    setDestinations(parsed);
                    // 恢复目的地后，需要重新查询目的地信息
                    // 为每个已保存的目的地查询信息（使用第一个目的地的前缀进行查询）
                    const firstDest = parsed[0];
                    if (firstDest && firstDest.length > 0) {
                        // 使用第一个目的地的前几个字符作为查询条件
                        const queryPrefix = firstDest.substring(0, Math.min(3, firstDest.length));
                        getWarehouseDetailsByDestination({
                            variables: { destination: queryPrefix },
                            onCompleted: (data) => {
                                const warehouses = data.getWarehouseDetailsByDestination || [];
                                const infoMap: DestinationInfo = {};
                                warehouses.forEach(warehouse => {
                                    if (warehouse.sortingCode) {
                                        infoMap[warehouse.sortingCode] = warehouse;
                                    }
                                    if (warehouse.name && warehouse.name !== warehouse.sortingCode) {
                                        infoMap[warehouse.name] = warehouse;
                                    }
                                });
                                setDestinationInfoMap(infoMap);
                            }
                        });
                    }
                }
            } catch (e) {
                console.error('Failed to parse saved destinations:', e);
            }
        }
        
        if (savedLocked === 'true') {
            setIsLocked(true);
        }
        const savedCarriers = localStorage.getItem(STORAGE_CARRIER_KEY);
        const savedCarrierLocked = localStorage.getItem(STORAGE_CARRIER_LOCK_KEY);
        if (savedCarriers) {
            try {
                const parsed = JSON.parse(savedCarriers);
                if (Array.isArray(parsed) && parsed.every((x): x is number => typeof x === 'number')) {
                    setCarrierSelectedValues(parsed);
                }
            } catch (e) {
                console.error('Failed to parse saved carriers:', e);
            }
        }
        if (savedCarrierLocked === 'true') {
            setIsCarrierLocked(true);
        }
    }, []);

    // 保存状态到localStorage
    useEffect(() => {
        if (destinations.length > 0) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(destinations));
        } else {
            localStorage.removeItem(STORAGE_KEY);
        }
    }, [destinations]);

    useEffect(() => {
        localStorage.setItem(STORAGE_LOCK_KEY, isLocked ? 'true' : 'false');
    }, [isLocked]);

    useEffect(() => {
        if (carrierSelectedValues.length > 0) {
            localStorage.setItem(STORAGE_CARRIER_KEY, JSON.stringify(carrierSelectedValues));
        } else {
            localStorage.removeItem(STORAGE_CARRIER_KEY);
        }
    }, [carrierSelectedValues]);

    useEffect(() => {
        localStorage.setItem(STORAGE_CARRIER_LOCK_KEY, isCarrierLocked ? 'true' : 'false');
    }, [isCarrierLocked]);


    const handleDestinationChange = (newDestinations: string[]) => {
        if (!isLocked) {
            setDestinations(newDestinations);
        }
    };

    // 处理删除目的地，移除匹配的包牌
    const handleDestinationRemove = (removedDestination: string) => {
        // 从scannedBags中移除与该目的地匹配的记录
        setScannedBags(prev => {
            const newBags = prev.filter(bag => bag.destination !== removedDestination);
            // 如果删除后没有包牌了，清空bagNumber
            if (newBags.length === 0) {
                setBagNumber('');
            }
            return newBags;
        });
    };

    const handleLockToggle = () => {
        setIsLocked(!isLocked);
    };

    const handleCarrierLockToggle = () => {
        setIsCarrierLocked(!isCarrierLocked);
    };

    const handleCarrierChange = (values: number[]) => {
        if (!isCarrierLocked) {
            setCarrierSelectedValues(values);
        }
    };

    const handleScanCore = async (code: string) => {
        if (!code.trim()) {
            return;
        }

        // 新页面：TT 开头视为包牌号（TTLAX1ATL...），其余沿用原有规则
        let isBagCode = isTtBagNumber(code) || isBagNumber(code);

        // 不是已知格式的包牌号，尝试查后端判断是否为外部包牌号
        let prefetchedBagInfo: BagBasicInfoType | null = null;
        if (!isBagCode) {
            try {
                const bagQueryResult = await getBagBasicInfo({ variables: { bagNumber: code } });
                const bagResp = bagQueryResult.data?.getBagBasicInfoByNumber;
                if (bagResp?.success && bagResp?.bagNumber) {
                    isBagCode = true;
                    prefetchedBagInfo = bagResp;
                }
            } catch {
                // 查询失败则走原有分流逻辑
            }
        }

        const isTrackingCode = !isBagCode && isTrackingNumber(code);

        if (isBagCode) {
            // 扫描的是包牌：目的地/下一站支持可选（为空时自动按包牌信息生成绑定目的地）

            // 从 ref 读最新 state（onProcess 是异步执行，闭包捕获的可能陈旧）
            const scannedBags = scannedBagsRef.current;
            const carrierSelectedValues = carrierSelectedValuesRef.current;
            const destinations = destinationsRef.current;
            const destinationInfoMap = destinationInfoMapRef.current;

            // 获取包牌基础信息（轻量接口，不拉订单列表）
            // 如果兜底判断时已查过，直接复用结果
            let bagInfo = prefetchedBagInfo;
            if (!bagInfo) {
                let bagQueryResult;
                try {
                    bagQueryResult = await getBagBasicInfo({ variables: { bagNumber: code } });
                } catch (error) {
                    console.error('Failed to fetch bag detail:', error);
                    playScanFailedAoSound();
                    scanPrompt.showResult(buildClientError(ClientError.BAG_NOT_FOUND), code);
                    return;
                }
                const bagResp = bagQueryResult.data?.getBagBasicInfoByNumber;
                if (!bagResp?.success) {
                    // 后端已渲染 promptInfo（如 BAG_NOT_EXIST），直接展示
                    playScanFailedAoSound();
                    if (bagResp?.promptInfo) {
                        scanPrompt.showResult({ success: false, data: { promptInfo: bagResp.promptInfo } }, code);
                    } else {
                        scanPrompt.showResult(buildClientError(ClientError.BAG_NOT_FOUND), code);
                    }
                    return;
                }
                bagInfo = bagResp;
            }
            if (!bagInfo?.bagNumber) {
                playScanFailedAoSound();
                scanPrompt.showResult(buildClientError(ClientError.BAG_SORTING_CODE_NOT_FOUND), code);
                return;
            }
            const validBagInfo = bagInfo;
            // 已通过 bagNumber 非空校验，此处断言为 string 安全
            const validBagNumber = validBagInfo.bagNumber as string;

            // 用内部包牌号做重复检测，避免外部包牌号绕过
            const scannedInternalBagNumbers = new Set(scannedBags.map(bag => bag.internalBagNumber));
            const scannedBagCodes = new Set(scannedBags.map(bag => bag.bagCode));
            if (scannedInternalBagNumbers.has(validBagNumber) || scannedBagCodes.has(code)) {
                playScanFailedAoSound();
                scanPrompt.showResult(buildClientError(ClientError.DUPLICATE_SCAN), code);
                return;
            }

            // 校验包牌状态：已废弃 / 已交接 不允许装包
            if (validBagInfo.status === BAG_STATUS_DISCARDED) {
                playScanFailedAoSound();
                scanPrompt.showResult(buildClientError(ClientError.BAG_DISCARDED, { status: 'DISCARDED' }), code);
                return;
            }
            if (validBagInfo.status === BAG_STATUS_HANDOVER) {
                playScanFailedAoSound();
                scanPrompt.showResult(buildClientError(ClientError.BAG_HANDOVER, { status: 'HANDOVER' }), code);
                return;
            }

            // 校验包牌仓归属：包牌必须是本仓创建的
            const whId = currentWarehouseIdRef.current;
            if (validBagInfo.warehouseId != null && whId != null && validBagInfo.warehouseId !== whId) {
                playScanFailedAoSound();
                scanPrompt.showResult(buildClientError(ClientError.BAG_NOT_BELONG), code);
                return;
            }

            // 分拣码允许为空（TT 包牌 sorting_code 合法为空）：不再拦截空分拣码；
            // 兜底为空串，避免下游按分拣码匹配（.startsWith）时对 undefined 报错
            const bagSortingCode = validBagInfo.sortingCode ?? '';

            // 如果选择了尾程商，则包牌尾程必须在所选列表内
            if (carrierSelectedValues.length > 0) {
                const bagCarrier = validBagInfo.carrierCode;
                if (bagCarrier == null || !carrierSelectedValues.includes(bagCarrier)) {
                    playScanFailedAoSound();
                    scanPrompt.showResult(buildClientError(
                        ClientError.BAG_CARRIER_NOT_MATCH,
                        { promptSortingCode: validBagInfo.promptSortingCode ?? bagSortingCode },
                    ), code);
                    return;
                }
            }

            // TT 业务类型判定：优先按业务类型(2=TT)，兜底按包牌号前缀 "TT"
            const isTtBag =
                validBagInfo.businessType === 2 ||
                validBagNumber.toUpperCase().startsWith('TT');
            // 面单样式为 SWX 时，整体覆盖和装包匹配都按 SWX 模式（sortingCode）；否则 TT 按 nextStop
            const useSwxMatchMode = validBagInfo.printType === PRINT_TYPE_SWIFTX;

            // 计算与当前包牌匹配的目的地列表
            let matchedDestinations: string[] = [];

            if (useSwxMatchMode || !isTtBag) {
                // SWX 模式：面单为 SWX 或非 TT 包牌，按 sortingCode 与目的地匹配
                if (destinations.length === 0) {
                    matchedDestinations = [bagSortingCode];
                } else {
                    matchedDestinations = [];
                    for (const dest of destinations) {
                        const destInfo = destinationInfoMap[dest];
                        if (!destInfo || !destInfo.sortingCode) {
                            continue;
                        }
                        const destSortingCode = destInfo.sortingCode;
                        if (destSortingCode === bagSortingCode) {
                            matchedDestinations.push(dest);
                            continue;
                        }
                        if (destInfo.siteType === 1 && bagSortingCode.startsWith(destSortingCode + '-')) {
                            matchedDestinations.push(dest);
                            continue;
                        }
                        if (destInfo.siteType === 2 && bagSortingCode.startsWith(destSortingCode + '-')) {
                            matchedDestinations.push(dest);
                        }
                    }
                }
                if (matchedDestinations.length === 0) {
                    playScanFailedAoSound();
                    scanPrompt.showResult(buildClientError(
                        ClientError.BAG_NO_MATCHING_DESTINATION,
                        { promptSortingCode: validBagInfo.promptSortingCode ?? bagSortingCode },
                    ), code);
                    return;
                }
            } else {
                // 纯 TT 模式：按 nextStop 与已选目的地匹配
                const rawNextStop = validBagInfo.nextStop;
                if (!rawNextStop) {
                    playScanFailedAoSound();
                    scanPrompt.showResult(buildClientError(ClientError.TT_BAG_NEXT_STOP_NOT_FOUND), code);
                    return;
                }
                const nextStop = rawNextStop.trim().toUpperCase();
                if (destinations.length === 0) {
                    matchedDestinations = [`${nextStop} (tt)`];
                } else {
                    // 仅精确匹配：nextStop 如 LAX1 只匹配 LAX1，不匹配 LAX（避免同一包牌匹配多条目的地）
                    matchedDestinations = destinations.filter(dest => {
                        const trimmed = dest.trim();
                        const base = trimmed.replace(/\s*\(tt\)\s*$/i, '').trim().toUpperCase();
                        return base === nextStop;
                    });
                }
                if (matchedDestinations.length === 0) {
                    playScanFailedAoSound();
                    scanPrompt.showResult(buildClientError(ClientError.TT_BAG_NEXT_STOP_NOT_MATCH), code);
                    return;
                }
            }

            // 检查是否需要替换已存在的包牌记录，或者是否有重叠错误
            const newCarrierKey = carrierSelectedValues.length > 0 ? (validBagInfo.carrierCode ?? -1) : -1;
            const bagsToReplace: ScannedBag[] = [];
            let hasOverlapError = false;

            // useSortingCodeReplace 是本次包牌属性：面单为 SWX 或非 TT 按分拣码，否则纯 TT 按下一站+尾程
            const useSortingCodeReplace = useSwxMatchMode || !isTtBag;
            if (useSortingCodeReplace) {
                // SWX 或 TT+SWX 打印：对整个已选列表做 sortingCode 重叠/替换校验，不按目的地分组，
                // 修复未选目的地时 LAX 与 LAX-DC 被分到不同组导致的跨组前缀重叠漏检
                for (const existingBag of scannedBags) {
                    if (hasOverlapError) break;
                    // 仅比较同一尾程分组（未选尾程时 carrierKey 恒为 -1）
                    const existingCarrierKey = carrierSelectedValues.length > 0 ? (existingBag.carrierCode ?? -1) : -1;
                    if (existingCarrierKey !== newCarrierKey) continue;
                    // 纯 TT 包牌(nextStop 有值且 printType 非 SWX)不参与分拣码重叠校验
                    const isPureTt = existingBag.nextStop != null && existingBag.printType != null && existingBag.printType !== PRINT_TYPE_SWIFTX;
                    if (isPureTt) continue;
                    const existingSortingCode = existingBag.sortingCode;
                    if (!existingSortingCode) continue;
                    if (bagSortingCode === existingSortingCode) {
                        bagsToReplace.push(existingBag);
                    } else if (
                        bagSortingCode.startsWith(existingSortingCode + '-') ||
                        existingSortingCode.startsWith(bagSortingCode + '-')
                    ) {
                        hasOverlapError = true;
                    }
                }
            } else {
                // 纯 TT 包牌：按 目的地|尾程|第三段服务码 分组，同下一站+尾程+服务码才视为需要替换；只替换纯 TT（nextStop 有值且 printType 非 SWX）
                // 第三段服务码取自 promptSortingCode（第一分拣规则，如 LAX1-LAX1-USMPM）的第 3 段，
                // 用于区分同尾程商下的服务级包牌（USMPM）与「不区分」包牌（枚举缩写 USM），避免二者被误判为同一包牌而弹「将更换包牌」
                const serviceSegOf = (pc?: string | null) => (pc || '').split('-')[2]?.trim().toUpperCase() || '';
                const newServiceSeg = serviceSegOf(validBagInfo.promptSortingCode);
                const bagsByDestAndCarrier = new Map<string, ScannedBag[]>();
                for (const bag of scannedBags) {
                    const carrierKey = carrierSelectedValues.length > 0 ? (bag.carrierCode ?? -1) : -1;
                    const key = `${bag.destination}|${carrierKey}|${serviceSegOf(bag.promptSortingCode)}`;
                    const bags = bagsByDestAndCarrier.get(key) || [];
                    bags.push(bag);
                    bagsByDestAndCarrier.set(key, bags);
                }
                for (const dest of matchedDestinations) {
                    const key = `${dest}|${newCarrierKey}|${newServiceSeg}`;
                    const existingBags = bagsByDestAndCarrier.get(key);
                    if (!existingBags || existingBags.length === 0) continue;
                    bagsToReplace.push(...existingBags.filter(b => b.nextStop != null && (b.printType == null || b.printType !== PRINT_TYPE_SWIFTX)));
                }
            }

            // 如果有重叠错误，先报错（仅 SWX）
            if (hasOverlapError) {
                playScanFailedAoSound();
                scanPrompt.showResult(buildClientError(ClientError.BAG_SORTING_CODE_OVERLAP), code);
                return;
            }

            // 已扫描包牌列表中的显示号：BAG 前缀用内部包牌号，其余（TT/外部）用原始扫描值
            const displayBagCode = isBagNumber(code) ? validBagNumber : code;

            // 如果有需要替换的记录，显示确认对话框
            if (bagsToReplace.length > 0) {
                const replaceDestination = bagsToReplace[0].destination;
                // 关闭上一个未确认的 Dialog（若有），让冲突确认串行化
                dialogHandleRef.current?.close();
                dialogHandleRef.current = Dialog.show({
                    title: t('pda.pages.bagging.replaceBagConfirm.title'),
                    content: (
                        <div className="text-center">
                            <div className="text-lg font-semibold mb-2">{replaceDestination}</div>
                            <div className="text-base">{t('pda.pages.bagging.replaceBagConfirm.message')}</div>
                        </div>
                    ),
                    closeOnAction: true,
                    actions: [
                        [
                            { key: 'cancel', text: t('pda.common.cancel') },
                            { key: 'confirm', text: t('pda.common.confirm'), bold: true }
                        ]
                    ],
                    onAction: (action) => {
                        dialogHandleRef.current = null;
                        if (action.key !== 'confirm') {
                            return;
                        }
                        const newScannedBags: ScannedBag[] = matchedDestinations.map(dest => ({
                            id: Date.now().toString() + '-' + Math.random().toString(36).slice(2, 11),
                            backendBagId: validBagInfo.id != null ? Number(validBagInfo.id) : null,
                            destination: dest,
                            bagCode: displayBagCode,
                            internalBagNumber: validBagNumber,
                            time: formatLocalizedTime(),
                            sortingCode: bagSortingCode,
                            carrierCode: validBagInfo.carrierCode ?? null,
                            printType: validBagInfo.printType ?? null,
                            // PPMOD 三件套：替换分支同样需要透传，否则替换后新 bag 丢失容器下拉
                            isPPMOD: !!validBagInfo.isPPMOD,
                            bagContainerId: validBagInfo.bagContainerId ?? null,
                            containerCandidates: validBagInfo.containerCandidates ?? [],
                            ...(isTtBag ? {
                    fromLocation: validBagInfo.fromLocation ?? null,
                    nextStop: validBagInfo.nextStop ?? null,
                    ttBizType: validBagInfo.ttBizType ?? null,
                    promptSortingCode: validBagInfo.promptSortingCode ?? null,
                } : {})
                        }));

                        setScannedBags(prev => {
                            const replaceIds = new Set(bagsToReplace.map(b => b.id));
                            const filtered = prev.filter(bag => !replaceIds.has(bag.id));
                            return [...newScannedBags, ...filtered];
                        });
                        setBagNumber(code);
                        setScanValue('');
                        scanPrompt.clearPrompt();
                        handleScanSuccessRef.current();
                        setIsScannedBagsExpanded(true);
                    }
                });
                return;
            }

            // 为每个匹配的目的地创建一个扫描记录
            const newScannedBags: ScannedBag[] = matchedDestinations.map(dest => ({
                id: Date.now().toString() + '-' + Math.random().toString(36).slice(2, 11),
                backendBagId: validBagInfo.id != null ? Number(validBagInfo.id) : null,
                destination: dest,
                bagCode: displayBagCode,
                internalBagNumber: validBagNumber,
                time: formatLocalizedTime(),
                sortingCode: bagSortingCode,
                carrierCode: validBagInfo.carrierCode ?? null,
                printType: validBagInfo.printType ?? null,
                // PPMOD 三件套：非 PP 时 candidates 为空数组，UI 层不渲染下拉
                isPPMOD: !!validBagInfo.isPPMOD,
                bagContainerId: validBagInfo.bagContainerId ?? null,
                containerCandidates: validBagInfo.containerCandidates ?? [],
                ...(isTtBag ? {
                    fromLocation: validBagInfo.fromLocation ?? null,
                    nextStop: validBagInfo.nextStop ?? null,
                    ttBizType: validBagInfo.ttBizType ?? null,
                    promptSortingCode: validBagInfo.promptSortingCode ?? null,
                } : {})
            }));

            setScannedBags(prev => [...newScannedBags, ...prev]);
            setBagNumber(code);
            setScanValue('');
            scanPrompt.clearPrompt();
            handleScanSuccessRef.current();
            setIsScannedBagsExpanded(true);
        } else if (isTrackingCode) {
            // 扫描的是运单号
            const scannedBags = scannedBagsRef.current;
            const carrierSelectedValues = carrierSelectedValuesRef.current;

            const clientReportKey = baggingScanClientReporter.createReportKey(code);
            void baggingScanClientReporter.onScanTriggered(clientReportKey, code);

            if (scannedBags.length === 0) {
                playScanFailedAoSound();
                scanPrompt.showResult(buildClientError(ClientError.PLEASE_BIND_BAG_FIRST), code);
                void baggingScanClientReporter.onIntercepted(clientReportKey, 'no_bag_bound');
                return;
            }

            // 尾程商筛选：若已选尾程商则只传该尾程商的包牌，否则传全部
            const bagsForCandidates = carrierSelectedValues.length > 0
                ? scannedBags.filter(bag => bag.carrierCode != null && carrierSelectedValues.includes(bag.carrierCode))
                : scannedBags;
            // 分拣码允许为空（TT 包牌 sorting_code 合法为空）：不再按分拣码过滤候选包牌，
            // 空分拣码的 TT 包牌由后端按 nextStop/尾程商匹配，故传空串即可
            const candidateBags: BaggingScanCandidateBagInput[] = bagsForCandidates
                .map(bag => ({
                    bagNumber: bag.internalBagNumber,
                    sortingCode: bag.sortingCode ?? '',
                    carrierCode: bag.carrierCode ?? null,
                    // PPMOD 装包：透传前端选中的容器 id；非 PP 场景后端会归一化为 null 不落库
                    bagContainerId: bag.bagContainerId ?? null,
                }));

            if (candidateBags.length === 0) {
                playScanFailedAoSound();
                scanPrompt.showResult(buildClientError(ClientError.NO_BAG_FOR_CARRIER), code);
                void baggingScanClientReporter.onIntercepted(clientReportKey, 'no_candidate_bag');
                return;
            }

            // 扫描包裹时，收起已绑定包牌清单
            setIsScannedBagsExpanded(false);
            scanPrompt.clearPrompt();

            void baggingScanClientReporter.onRequest(clientReportKey, { trackingNumber: code, candidateBags });
            let responseData: any = null;
            try {
                const result = await handleWhsBaggingScanEvent({
                    variables: { trackingNumber: code, candidateBags, clientReportKey }
                });
                // mutation 直接返回 BaggingScanResponse（无外壳），result 本身即为 data 载体
                const raw = result.data?.result;
                const envelope = raw ? { success: raw.success, message: raw.message ?? null, data: raw } : undefined;
                responseData = raw;
                const matchedBagNumber = raw?.matchedBagNumber;
                if (envelope?.success && matchedBagNumber) {
                    clearCurrentError();
                    scanPrompt.showResult(envelope as ScanResultEnvelope<BaggingScanResponse>, code);
                    setSuccessRecords(prev => [createRecord({
                        value1: matchedBagNumber,
                        value2: code
                    }), ...prev]);
                    handleScanSuccessRef.current();
                } else if (envelope) {
                    // 业务失败：走 ScanResultPrompt（红/橙/黑底 SortingScan 同款样式），promptInfo 已由后端渲染
                    scanPrompt.showResult(envelope as ScanResultEnvelope<BaggingScanResponse>, code);
                    if (envelope.data?.promptInfo?.messageKey !== 'ALREADY_SCANNED') {
                        playScanFailedAoSound();
                    }
                    const pi = envelope.data?.promptInfo;
                    const reason = [pi?.title, pi?.content].filter(Boolean).join('，') || envelope.message || (t('pda.common.failed') || '扫描失败');
                    setFailedRecords(prev => [createRecord({
                        value1: '',
                        value2: code,
                        failedReason: reason
                    }), ...prev]);
                }
                setScanValue('');
            } catch (error: any) {
                // 网络 / Apollo 异常兜底
                const { message } = parseException(error);
                scanPrompt.showResult(buildClientError(ClientError.NETWORK_ERROR, {}, ClientError.BAGGING_SCAN_TITLE), code);
                playScanFailedAoSound();
                responseData = { error: String(error), message };
                setFailedRecords(prev => [createRecord({
                    value1: '',
                    value2: code,
                    failedReason: message
                }), ...prev]);
                setScanValue('');
            } finally {
                baggingScanClientReporter.onResponse(clientReportKey, responseData || {})
                    .catch(() => {
                        void baggingScanClientReporter.reportDirectly(clientReportKey, {
                            responseAtMs: Date.now(),
                            responseResult: responseData ? truncateToLength(safeJsonStringify(responseData), 300) : ''
                        });
                    });
            }
        } else {
            showBarCodeError(code);
        }
    };

    // 队列消费入口
    const onProcess = async (event: PdaScanEvent) => {
        clearCurrentError();
        handleScanStartRef.current();
        try {
            await handleScanCore(event.code);
        } catch (e) {
            console.error('handleScanCore error:', e);
        }
    };

    const { enqueueScan } = useScanQueueConsumer({ maxConcurrency: 1, onProcess });

    const handleScanButtonClick = () => {
        // TODO: 打开扫描器
        console.log('Open scanner');
    };

    const handleRemoveScannedBag = (id: string) => {
        setScannedBags(prev => {
            const newBags = prev.filter(bag => bag.id !== id);
            // 如果删除后没有包牌了，清空bagNumber
            if (newBags.length === 0) {
                setBagNumber('');
            }
            return newBags;
        });
    };

    const successColumns: Column[] = [
        { title: t('pda.pages.bagging.bagCode'), key: 'value1' },
        { title: t('pda.common.trackingNO'), key: 'value2' },
        { title: t('pda.common.time'), key: 'time' }
    ];

    const failedColumns: Column[] = [
        { title: t('pda.common.trackingNO'), key: 'value2' },
        { title: t('pda.common.time'), key: 'time' },
        { title: t('pda.common.failedReason'), key: 'failedReason' }
    ];

    return (
        <div className="flex flex-col gap-2">
            {/* 尾程选择 */}
            <div className="mb-2">
                <CarrierSelector
                    label={t('pda.pages.bagging.carrierLabel') ?? ''}
                    options={carrierOptions}
                    selectedValues={carrierSelectedValues}
                    onChange={handleCarrierChange}
                    isLocked={isCarrierLocked}
                    onLockToggle={handleCarrierLockToggle}
                />
            </div>

            {/* 下一站选择 */}
            <div className="mb-2">
                <DestinationSelector
                    label={t('pda.pages.bagging.nextStopLabel') ?? ''}
                    destinations={destinations}
                    isLocked={isLocked}
                    onChange={handleDestinationChange}
                    onLockToggle={handleLockToggle}
                    onDestinationInfoChange={setDestinationInfoMap}
                    onDestinationRemove={handleDestinationRemove}
                />
            </div>

            {/* 扫描包裹框 */}
            <div className="mb-2">
                <ScanInput
                    ref={scanInputRef}
                    placeholder={t('pda.pages.bagging.scanOrInput')}
                    value={scanValue}
                    onScan={enqueueScan}
                    onClick={handleScanButtonClick}
                />
            </div>

            {/* 已扫描包牌列表 */}
            {scannedBags.length > 0 && (
                <div className="mb-2 border rounded">
                    <div
                        className="p-2 bg-blue-50 border-b flex items-center justify-between cursor-pointer"
                        onClick={() => setIsScannedBagsExpanded(!isScannedBagsExpanded)}
                    >
                        <span className="text-sm font-medium text-blue-800 flex-1 text-center">
                            {t('pda.pages.bagging.selectedBags', { count: scannedBags.length })}
                        </span>
                        {isScannedBagsExpanded ? (
                            <UpOutline className="text-blue-800" fontSize={16} />
                        ) : (
                            <DownOutline className="text-blue-800" fontSize={16} />
                        )}
                    </div>
                    {isScannedBagsExpanded && (
                        <div className="max-h-60 overflow-y-auto">
                            <div className="grid grid-cols-[70px_1fr_1fr_28px] gap-2 px-2 py-2 border-b bg-gray-50 text-[11px] leading-snug text-gray-500 items-start">
                                <span className="min-w-0 break-words">{t('pda.pages.bagging.carrier')}</span>
                                <span className="min-w-0 break-words">{t('pda.pages.bagging.sortingCodeOrDestination')}</span>
                                <span className="min-w-0 break-words">{t('pda.pages.bagging.bagCode')}</span>
                                <span />
                            </div>
                            {scannedBags.map((bag) => {
                                const carrierName = bag.carrierCode != null
                                    ? (carrierOptions.find(c => c.value === bag.carrierCode)?.name ?? String(bag.carrierCode))
                                    : '-';
                                // TT 包牌：SortingRule 与预分拣码拼接串（promptSortingCode）优先展示
                                const sortingCodeText = bag.promptSortingCode || bag.sortingCode || bag.destination;
                                const ttBizTypeText = bag.ttBizType
                                    ? (bag.ttBizType === 'UNLIMITED'
                                        ? t('pda.pages.bagging.bizTypeUnlimited')
                                        : bag.ttBizType)
                                    : null;
                                // 仅当后端返回容器候选（PP 包）时展示行级容器下拉
                                const showContainerPicker = (bag.containerCandidates?.length ?? 0) > 0;
                                const selectedContainerName = bag.bagContainerId != null
                                    ? (bag.containerCandidates?.find(c => c.id === bag.bagContainerId)?.name || '')
                                    : '';
                                return (
                                    <div key={bag.id} className="border-b last:border-b-0">
                                        <div className="grid grid-cols-[70px_1fr_1fr_28px] gap-2 px-2 py-2 items-start hover:bg-gray-50">
                                            <span className="text-xs text-gray-600 min-w-0 break-words flex flex-col items-start gap-0.5">
                                                <span>{carrierName}</span>
                                                {ttBizTypeText && (
                                                    <span className="px-1 rounded bg-gray-800 text-white text-[10px] whitespace-nowrap">
                                                        {ttBizTypeText}
                                                    </span>
                                                )}
                                            </span>
                                            <span className="text-xs font-medium text-gray-700 min-w-0 break-words">
                                                {sortingCodeText}
                                            </span>
                                            <span className="text-xs text-gray-900 min-w-0 break-all">
                                                {bag.bagCode}
                                            </span>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveScannedBag(bag.id);
                                                }}
                                                className="shrink-0 text-gray-400 hover:text-red-500 text-base font-bold leading-none w-6 h-6 flex items-center justify-center justify-self-end"
                                            >
                                                ×
                                            </button>
                                        </div>
                                        {showContainerPicker && (
                                            <div
                                                className="mx-2 mb-2 border rounded p-2 flex items-center cursor-pointer"
                                                style={{ fontSize: '10px' }}
                                                onClick={() => setContainerPickerBagId(bag.id)}
                                            >
                                                <span
                                                    className="flex-1"
                                                    style={{ color: bag.bagContainerId == null ? '#999' : '#000' }}
                                                >
                                                    {bag.bagContainerId == null
                                                        ? (t('pda.pages.bagging.selectContainerType') || 'Please select container type')
                                                        : selectedContainerName}
                                                </span>
                                                <DownOutline fontSize={16} style={{ color: '#999' }} />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* 行级容器 Picker：由 containerPickerBagId 定位到具体 bag；同一时刻仅一个 Picker 可见 */}
            {(() => {
                const targetBag = containerPickerBagId != null
                    ? scannedBags.find(b => b.id === containerPickerBagId)
                    : null;
                const candidates = targetBag?.containerCandidates ?? [];
                return (
                    <Picker
                        columns={[candidates.map(c => ({ label: c.name, value: c.id }))]}
                        visible={containerPickerBagId != null && candidates.length > 0}
                        onClose={() => setContainerPickerBagId(null)}
                        value={targetBag?.bagContainerId != null ? [targetBag.bagContainerId] : []}
                        title={t('pda.pages.bagging.selectContainerType') || 'Please select container type'}
                        onConfirm={async (value: PickerValue[]) => {
                            if (!targetBag) return;
                            const next = value[0] as number | null;
                            if (next == null || next === targetBag.bagContainerId) return;
                            // 已选过容器再改 → 二次确认（对齐单流向）
                            if (targetBag.bagContainerId != null) {
                                const newName = candidates.find(c => c.id === next)?.name || '';
                                const tmpl = t('pda.pages.bagging.changeContainerConfirm')
                                    || '{{sortingCode}} {{bagNumber}} will be changed to {{containerType}}';
                                const msg = tmpl
                                    .replace('{{sortingCode}}', targetBag.sortingCode || targetBag.destination || '-')
                                    .replace('{{bagNumber}}', targetBag.bagCode || '-')
                                    .replace('{{containerType}}', newName);
                                const ok = await Dialog.confirm({
                                    title: t('pda.pages.bagging.changeContainerTitle') || 'Change container type',
                                    content: msg,
                                    confirmText: t('pda.common.confirm'),
                                    cancelText: t('pda.common.cancel'),
                                });
                                if (!ok) return;
                            }
                            // 缺少后端 bag.id 无法调 updateBagContainer（老数据/异常路径兜底）：
                            // 走 ScanResultPrompt 错误级弹层，仅展示 title（i18n content 为空自动隐藏），不改本地 state
                            if (targetBag.backendBagId == null) {
                                playScanFailedAoSound();
                                scanPrompt.showResult(
                                    buildClientError(ClientError.CHANGE_CONTAINER_NO_BAG_ID),
                                    targetBag.bagCode || '',
                                );
                                return;
                            }
                            // 乐观更新本地 state，同时调用 PDA 版 updateBagContainerPDA 落库；
                            // 失败：优先展示后端渲染的 promptInfo（照抄 OldBaggingScanPage.tsx:284-285 模式），
                            // 兜底走 buildClientError（i18n_config 未 seed 或 catch 到系统级异常时）
                            const prevContainerId = targetBag.bagContainerId ?? null;
                            setScannedBags(prev => prev.map(b =>
                                b.id === targetBag.id ? { ...b, bagContainerId: next } : b
                            ));
                            try {
                                const resp = await updateBagContainerPda({
                                    variables: { bagId: targetBag.backendBagId, bagContainerId: next },
                                });
                                const result = resp.data?.updateBagContainerPDA;
                                if (!result?.success) {
                                    setScannedBags(prev => prev.map(b =>
                                        b.id === targetBag.id ? { ...b, bagContainerId: prevContainerId } : b
                                    ));
                                    playScanFailedAoSound();
                                    if (result?.promptInfo) {
                                        scanPrompt.showResult(
                                            { success: false, data: { promptInfo: result.promptInfo } },
                                            targetBag.bagCode || '',
                                        );
                                    } else {
                                        scanPrompt.showResult(
                                            buildClientError(
                                                ClientError.CHANGE_CONTAINER_FAILED,
                                                { errorMessage: '' },
                                            ),
                                            targetBag.bagCode || '',
                                        );
                                    }
                                } else if (result.promptInfo) {
                                    // 成功：复用后端 runScanDirect 渲染的 SUCCESS promptInfo，
                                    // 走 ScanResultPrompt 成功样式，与其它扫码成功保持一致
                                    scanPrompt.showResult(
                                        { success: true, data: { promptInfo: result.promptInfo } },
                                        targetBag.bagCode || '',
                                    );
                                }
                            } catch (e) {
                                setScannedBags(prev => prev.map(b =>
                                    b.id === targetBag.id ? { ...b, bagContainerId: prevContainerId } : b
                                ));
                                playScanFailedAoSound();
                                scanPrompt.showResult(
                                    buildClientError(
                                        ClientError.CHANGE_CONTAINER_FAILED,
                                        { errorMessage: (e as Error)?.message || '' },
                                    ),
                                    targetBag.bagCode || '',
                                );
                            }
                        }}
                    />
                );
            })()}

            {/* 结果列表区域 */}
            <ResultTable
                successRecords={successRecords}
                failedRecords={failedRecords}
                successColumns={successColumns}
                failedColumns={failedColumns}
            />
        </div>
    );
};

export default function BaggingScanPage() {
    const { t } = useTranslation();
    const scanPrompt = useScanResultPrompt();
    const borderElRef = useRef<HTMLDivElement | null>(null);
    const borderTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const setBorderColor = (color: string) => {
        const el = borderElRef.current;
        if (!el) return;
        el.style.outlineColor = color;
    };

    const clearBorderTimeout = () => {
        if (borderTimeoutRef.current) {
            clearTimeout(borderTimeoutRef.current);
            borderTimeoutRef.current = null;
        }
    };

    const handleScanStart = () => {
        clearBorderTimeout();
        setBorderColor(DEFAULT_BORDER_COLOR);
    };

    const handleScanSuccess = () => {
        clearBorderTimeout();
        setBorderColor(SUCCESS_BORDER_COLOR);
        borderTimeoutRef.current = setTimeout(() => {
            setBorderColor(DEFAULT_BORDER_COLOR);
            borderTimeoutRef.current = null;
        }, 2000); // 持续2秒
    };

    // 仅在页面挂载时创建一次全屏边框 DOM；后续只改样式，避免触发重渲染
    useEffect(() => {
        const el = document.createElement('div');
        el.setAttribute('data-pda-bagging-success-border', '1');
        el.style.position = 'fixed';
        el.style.inset = '0';
        el.style.pointerEvents = 'none';
        el.style.zIndex = '9999';
        el.style.outlineStyle = 'solid';
        el.style.outlineWidth = '6px';
        el.style.outlineColor = DEFAULT_BORDER_COLOR;
        el.style.outlineOffset = '-6px';
        el.style.borderRadius = '8px';
        el.style.transition = 'outline-color 150ms ease';

        document.body.appendChild(el);
        borderElRef.current = el;

        return () => {
            clearBorderTimeout();
            borderElRef.current = null;
            el.remove();
        };
    }, []);

    return (
        <div className="w-full h-full flex flex-col gap-2 p-2 relative">
            {/* 业务失败 / 系统异常的红/橙/黑底反馈层（与 SortingScan 同款），z-[60] 盖在原有边框上 */}
            <ScanResultPrompt
                prompt={scanPrompt.prompt}
                barCode={scanPrompt.barCode}
                onClose={scanPrompt.clearPrompt}
            />
            <Header title={t('pda.pages.bagging.baggingScan')} />
            <Pda />
            <PageBody
                handleScanStart={handleScanStart}
                handleScanSuccess={handleScanSuccess}
                scanPrompt={scanPrompt}
            />
        </div>
    );
}

